import { computed, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { useAuthenticationStore } from '@/features/auth/stores/authenticationStore.js';
import { useWorkspaceStore } from '@/features/workspaces/stores/workspaceStore.js';
import { useOrganizationStore } from '@/features/organizations/stores/organizationStore.js';
import { useClient } from '@/plugins/api.js';

export const permissionKeys = {
    contentRead: 'Content.Read',
    contentWrite: 'Content.Write',
    workspaceWrite: 'Workspaces.Write',
    approvalsWrite: 'Approvals.Write',
};

export const usePermissionsStore = defineStore('permissions', () => {
    const client = useClient();
    const auth = useAuthenticationStore();
    const workspaceStore = useWorkspaceStore();
    const organizationStore = useOrganizationStore();

    const context = ref(null);
    const isLoading = ref(false);
    const error = ref(null);
    let fetchPromise = null;

    const permissions = computed(() => context.value?.permissions ?? []);

    function has(permission) {
        return permissions.value.includes(permission);
    }

    async function fetchContext({ force = false } = {}) {
        if (!auth.isAuthenticated) {
            context.value = null;
            error.value = null;
            return null;
        }

        if (fetchPromise && !force) {
            return fetchPromise;
        }

        isLoading.value = true;
        error.value = null;
        fetchPromise = client
            .get('/api/users/context', {
                params: {
                    organizationId: organizationStore.selectedOrganizationId ?? undefined,
                    workspaceId: workspaceStore.activeWorkspaceId ?? undefined,
                },
            })
            .then(response => {
                context.value = response.data ?? null;
                return context.value;
            })
            .catch(fetchError => {
                console.error('Failed to fetch user context:', fetchError);
                context.value = null;
                error.value = 'Failed to load user permissions.';
                return null;
            })
            .finally(() => {
                isLoading.value = false;
                fetchPromise = null;
            });

        return fetchPromise;
    }

    watch(
        () => [auth.isAuthenticated, organizationStore.selectedOrganizationId, workspaceStore.activeWorkspaceId],
        async ([isAuthenticated]) => {
            if (!isAuthenticated) {
                context.value = null;
                error.value = null;
                return;
            }

            await fetchContext({ force: true });
        },
        { immediate: true }
    );

    return {
        context,
        permissions,
        isLoading,
        error,
        fetchContext,
        has,
    };
});
